import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Container,
  Spinner,
} from "reactstrap";
import PlaceholderSidebar from "./PlaceholderSidebar";
import { useTranslation } from "context/TranslationProvider";
import { useNavigate } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { useNotification } from "providers/NotificationProvider";
import NotificationTemplatePreviewModal from "../NotificationTemplatePreviewModal";
import CreatableSelect from "react-select/creatable";
import helpers from "utils/helpers";

const TINYMCE_SCRIPT_SRC = "https://tinymce.appfoster.com/tinymce.min.js";

const CreateTemplatePage = ({
  loading,
  templateData,
  setTemplateData,
  placeholders,
  isEditMode,
  onSubmit,
  onPreview,
}) => {
  const [previewHtml, setPreviewHtml] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const editorRef = useRef(null);
  const subjectRef = useRef(null);
  const rightCardRef = useRef(null);
  const [rightCardHeight, setRightCardHeight] = useState(null);
  const [activeField, setActiveField] = useState(null);

  useEffect(() => {
    if (!rightCardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setRightCardHeight(entry.target.offsetHeight);
      }
    });
    resizeObserver.observe(rightCardRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const navigate = useNavigate();
  const { notify } = useNotification();
  const channelOptions = helpers.getNotificationChannelOptions(t);

  const handleBack = () => {
    navigate(-1);
  };

  const handleFocus = (fieldName) => setActiveField(fieldName);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTemplateData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditorChange = (content) => {
    setTemplateData((prev) => ({ ...prev, content }));
  };

  const insertPlaceholder = (placeholder) => {
    if (activeField === "body" && editorRef.current) {
      editorRef.current.insertContent(placeholder);
    } else if (activeField === "subject" && subjectRef.current) {
      const input = subjectRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const newValue =
        templateData.subject.substring(0, start) +
        placeholder +
        templateData.subject.substring(end);

      setTemplateData((prev) => ({ ...prev, subject: newValue }));

      setTimeout(() => {
        input.focus();
        input.selectionStart = input.selectionEnd =
          start + placeholder.length;
      }, 0);
    }
  };

  const handlePreviewClick = async () => {
    if (
      !templateData.name?.trim() ||
      !templateData.subject?.trim() ||
      !templateData.content ||
      templateData.content === "<p><br></p>" ||
      templateData.content === ""
    ) {
      notify("warning", t("page.notificationTemplates.toast.allFieldsRequired", "All fields are required."));
      return;
    }

    const html = await onPreview();

    if (html) {
      setPreviewHtml(html);
      setIsPreviewOpen(true);
    }
  };

  const handleSubmitClick = () => {
    onSubmit(templateData);
  };

  const previewClickRef = useRef(handlePreviewClick);
  useEffect(() => {
    previewClickRef.current = handlePreviewClick;
  });

  const editorInit = useMemo(() => {
    return {
      height: 550,
      menubar: false,
      license_key: "gpl",
      plugins: "lists link code table",
      toolbar: [
        "blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | removeformat",
        "alignleft aligncenter alignright | bullist numlist | outdent indent | link insertBtnAction editStyleBtn table hr | code | previewBtn",
      ],
      font_family_formats:
        "Arial=arial,helvetica,sans-serif; " +
        "Georgia=georgia,palatino,serif; " +
        "Helvetica=helvetica,arial,sans-serif; " +
        "Tahoma=tahoma,arial,helvetica,sans-serif; " +
        "Times New Roman=times new roman,times,serif; " +
        "Trebuchet MS=trebuchet ms,geneva,sans-serif; " +
        "Verdana=verdana,geneva,sans-serif",
      font_size_formats: "10px 12px 14px 16px 18px 20px 24px 28px 32px 36px",
      branding: false,
      promotion: false,
      statusbar: true,
      content_style: `
        body {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: #172b4d;
        }
        table {
          border-collapse: collapse;
          width: 100%;
        }
        table td, table th {
          border: 1px solid #dee2e6;
          padding: 8px 12px;
        }
        .email-btn {
          display: inline-block;
          padding: 10px 24px;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          color: #ffffff;
          text-align: center;
          cursor: pointer;
        }
      `,
      setup: (editor) => {
        // --- Register Custom Icons ---
        editor.ui.registry.addIcon("smart-button", `
          <svg width="24" height="24" viewBox="0 0 24 24">
            <path d="M22 9h-2V7h2v2zm0-4h-2V3h2v2zm0 8h-2v-2h2v2zm-4-8h-2V3h2v2zm0 12h-2v-2h2v2zM10.8 19H1.2C.54 19 0 18.46 0 17.8V6.2C0 5.54.54 5 1.2 5h9.6c.66 0 1.2.54 1.2 1.2v11.6c0 .66-.54 1.2-1.2 1.2zm-1-12.8H2.2v10.4h7.6V6.2z" fill="currentColor"></path>
          </svg>
        `);

        editor.ui.registry.addIcon("view-stream", `
          <svg width="24" height="24" viewBox="0 0 24 24">
            <path d="M4 18h17v-6H4v6zM4 5v6h17V5H4z" fill="currentColor"></path>
          </svg>
        `);

        // --- Helper: parse inline style string into an object ---
        const parseStyle = (styleStr) => {
          const styles = {};
          if (!styleStr) return styles;
          styleStr.split(";").forEach((rule) => {
            const [key, ...valParts] = rule.split(":");
            if (key && valParts.length) {
              styles[key.trim()] = valParts.join(":").trim();
            }
          });
          return styles;
        };

        // --- Helper: convert rgb/rgba/color names to hex ---
        const toHex = (colorStr) => {
          if (!colorStr) return "";
          const trimmed = colorStr.trim().toLowerCase();
          if (trimmed.startsWith("#")) return trimmed;
          
          // Handle rgb/rgba
          const rgbMatch = trimmed.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/);
          if (rgbMatch) {
            const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, "0");
            const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, "0");
            const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, "0");
            return `#${r}${g}${b}`;
          }

          // Common color names mapping
          const colorNames = {
            black: "#000000",
            white: "#ffffff",
            red: "#ff0000",
            green: "#008000",
            blue: "#0000ff",
            yellow: "#ffff00",
            gray: "#808080",
            grey: "#808080",
            silver: "#c0c0c0",
            maroon: "#800000",
            purple: "#800080",
            fuchsia: "#ff00ff",
            lime: "#00ff00",
            olive: "#808000",
            navy: "#000080",
            teal: "#008080",
            aqua: "#00ffff",
            orange: "#ffa500"
          };
          return colorNames[trimmed] || colorStr;
        };

        // --- Helper: detect if an <a> tag is a button ---
        const isButton = (el) => {
          if (!el || el.tagName !== "A") return false;
          if (el.classList.contains("email-btn")) return true;
          const s = parseStyle(el.getAttribute("style") || "");
          return Boolean(
            s["background-color"] ||
            s["background"] ||
            s["padding"] ||
            s["border-radius"] ||
            s["display"] === "inline-block" ||
            s["display"] === "block"
          );
        };

        // --- Helper: open button dialog (insert or edit) ---
        const widthPresets = ["auto", "100%", "120px", "200px", "300px"];

        const openButtonDialog = (existingEl) => {
          const isEdit = Boolean(existingEl);
          let existing = {};

          if (isEdit) {
            const s = parseStyle(existingEl.getAttribute("style") || "");
            existing = {
              btnText: existingEl.textContent || "Click Here",
              btnUrl: existingEl.getAttribute("href") || "{{targetLink}}",
              btnColor: toHex(s["background-color"] || s["background"] || "#f5a623"),
              btnTextColor: toHex(s["color"] || "#ffffff"),
              btnFontSize: s["font-size"] || "14px",
              btnPadding: s["padding"] || "10px 24px",
              btnWidth: s["width"] || "auto",
              btnRadius: s["border-radius"] || "6px",
              btnMargin: s["margin"] || "0 8px 0 0",
            };
            // Map width to selectbox — if custom value, use "custom"
            if (!widthPresets.includes(existing.btnWidth)) {
              existing._customWidth = existing.btnWidth;
              existing.btnWidth = "custom";
            }
          }

          editor.windowManager.open({
            title: isEdit ? "Edit Button" : "Insert Button",
            body: {
              type: "panel",
              items: [
                {
                  type: "input",
                  name: "btnText",
                  label: "Button Text/Label",
                },
                {
                  type: "input",
                  name: "btnUrl",
                  label: "Button URL",
                  placeholder: "https://...",
                },
                {
                  type: "colorinput",
                  name: "btnColor",
                  label: "Background Color",
                },
                {
                  type: "colorinput",
                  name: "btnTextColor",
                  label: "Text Color",
                },
                {
                  type: "input",
                  name: "btnFontSize",
                  label: "Font Size (e.g. 14px)",
                },
                {
                  type: "input",
                  name: "btnPadding",
                  label: "Padding (e.g. 10px 24px)",
                },
                {
                  type: "selectbox",
                  name: "btnWidth",
                  label: "Width",
                  items: [
                    { text: "Auto", value: "auto" },
                    { text: "Full Width (100%)", value: "100%" },
                    { text: "Small (120px)", value: "120px" },
                    { text: "Medium (200px)", value: "200px" },
                    { text: "Large (300px)", value: "300px" },
                    { text: "Custom", value: "custom" },
                  ],
                },
                {
                  type: "input",
                  name: "btnCustomWidth",
                  label: "Custom Width (e.g. 250px)",
                },
                {
                  type: "input",
                  name: "btnRadius",
                  label: "Border Radius (e.g. 6px)",
                },
                {
                  type: "input",
                  name: "btnMargin",
                  label: "Margin (e.g. 0 8px 0 0)",
                },
              ],
            },
            buttons: [
              { type: "cancel", text: "Cancel" },
              isEdit ? { type: "custom", name: "deleteBtn", text: "Remove Button", primary: false } : null,
              { type: "submit", text: isEdit ? "Update" : "Insert", primary: true },
            ].filter(Boolean),
            initialData: {
              btnText: existing.btnText || "Click Here",
              btnUrl: existing.btnUrl || "{{targetLink}}",
              btnColor: existing.btnColor || "#f5a623",
              btnTextColor: existing.btnTextColor || "#ffffff",
              btnFontSize: existing.btnFontSize || "14px",
              btnPadding: existing.btnPadding || "10px 24px",
              btnWidth: existing.btnWidth || "auto",
              btnCustomWidth: existing._customWidth || "",
              btnRadius: existing.btnRadius || "6px",
              btnMargin: existing.btnMargin || "0 8px 0 0",
            },
            onAction: (api, details) => {
              if (details.name === "deleteBtn") {
                if (existingEl) {
                  existingEl.remove();
                  editor.undoManager.add();
                  editor.nodeChanged();
                }
                api.close();
              }
            },
            onSubmit: (api) => {
              const d = api.getData();
              const finalWidth = d.btnWidth === "custom" ? d.btnCustomWidth : d.btnWidth;
              const widthStyle = finalWidth && finalWidth !== "auto" ? `width:${finalWidth};` : "";
              
              const parts = [
                "display:inline-block",
                `padding:${d.btnPadding}`,
                `border-radius:${d.btnRadius}`,
                "text-decoration:none",
                "font-weight:600",
                `font-size:${d.btnFontSize}`,
                `color:${d.btnTextColor}`,
                `text-align:center`,
                `background-color:${d.btnColor}`,
              ];
              if (d.btnMargin) {
                parts.push(`margin:${d.btnMargin}`);
              }
              if (widthStyle) {
                parts.push(widthStyle.replace(";", ""));
              }

              if (isEdit && existingEl) {
                const s = parseStyle(existingEl.getAttribute("style") || "");
                const managedBtnStyles = [
                  "display",
                  "padding",
                  "border-radius",
                  "text-decoration",
                  "font-weight",
                  "font-size",
                  "color",
                  "text-align",
                  "background-color",
                  "background",
                  "margin",
                  "width"
                ];
                Object.entries(s).forEach(([key, val]) => {
                  if (!managedBtnStyles.includes(key) && val) {
                    parts.push(`${key}:${val}`);
                  }
                });

                existingEl.setAttribute("href", d.btnUrl);
                existingEl.setAttribute("style", parts.join(";"));
                existingEl.textContent = d.btnText;
                existingEl.classList.add("email-btn");
                editor.undoManager.add();
                editor.nodeChanged();
              } else {
                const style = parts.join(";");
                const btnHtml = `<a href="${d.btnUrl}" class="email-btn" style="${style}" target="_blank">${d.btnText}</a>`;
                editor.insertContent(btnHtml);
              }
              api.close();
            },
          });
        };

        // --- Preview button ---
        editor.ui.registry.addButton("previewBtn", {
          icon: "preview",
          tooltip: tRef.current("page.notificationTemplates.buttons.preview"),
          onAction: () => {
            previewClickRef.current();
          },
        });

        // --- Insert button (toolbar) ---
        editor.ui.registry.addButton("insertBtnAction", {
          icon: "smart-button",
          tooltip: "Insert Button",
          onAction: () => openButtonDialog(null),
        });

        // --- Helper: open element style editor dialog ---
        const openStyleDialog = (el) => {
          if (!el || el.tagName === "BODY") return;
          const s = parseStyle(el.getAttribute("style") || "");

          editor.windowManager.open({
            title: "Edit Element Style",
            body: {
              type: "panel",
              items: [
                { type: "input", name: "elPadding", label: "Padding (e.g. 12px 16px)" },
                { type: "input", name: "elMargin", label: "Margin (e.g. 0 0 12px 0)" },
                { type: "input", name: "elWidth", label: "Width (e.g. 100%, 300px, auto)" },
                { type: "input", name: "elHeight", label: "Height (e.g. auto, 200px)" },
                { type: "colorinput", name: "elBgColor", label: "Background Color" },
                { type: "colorinput", name: "elTextColor", label: "Text Color" },
                { type: "input", name: "elBorder", label: "Border (e.g. 1px solid #ccc)" },
                { type: "input", name: "elBorderRadius", label: "Border Radius (e.g. 8px)" },
              ],
            },
            buttons: [
              { type: "cancel", text: "Cancel" },
              { type: "custom", name: "deleteElement", text: "Remove Element", primary: false },
              { type: "submit", text: "Apply", primary: true },
            ],
            initialData: {
              elPadding: s["padding"] || "",
              elMargin: s["margin"] || "",
              elWidth: s["width"] || "",
              elHeight: s["height"] || "",
              elBgColor: toHex(s["background-color"] || s["background"] || ""),
              elTextColor: toHex(s["color"] || ""),
              elBorder: s["border"] || "",
              elBorderRadius: s["border-radius"] || "",
            },
            onAction: (api, details) => {
              if (details.name === "deleteElement") {
                if (el) {
                  el.remove();
                  editor.undoManager.add();
                  editor.nodeChanged();
                }
                api.close();
              }
            },
            onSubmit: (api) => {
              const d = api.getData();
              const parts = [];
              if (d.elPadding) parts.push(`padding:${d.elPadding}`);
              if (d.elMargin) parts.push(`margin:${d.elMargin}`);
              if (d.elWidth) parts.push(`width:${d.elWidth}`);
              if (d.elHeight) parts.push(`height:${d.elHeight}`);
              if (d.elBgColor) parts.push(`background-color:${d.elBgColor}`);
              if (d.elTextColor) parts.push(`color:${d.elTextColor}`);
              if (d.elBorder) parts.push(`border:${d.elBorder}`);
              if (d.elBorderRadius) parts.push(`border-radius:${d.elBorderRadius}`);

              // Preserve any styles we don't manage
              const managed = ["padding", "margin", "width", "height", "background-color", "background", "color", "border", "border-radius"];
              Object.entries(s).forEach(([key, val]) => {
                if (!managed.includes(key) && val) {
                  parts.push(`${key}:${val}`);
                }
              });

              el.setAttribute("style", parts.join(";"));
              editor.undoManager.add();
              editor.nodeChanged();
              api.close();
            },
          });
        };

        // --- Edit style toolbar button ---
        editor.ui.registry.addButton("editStyleBtn", {
          icon: "view-stream",
          tooltip: "Edit Element Style/Button",
          onAction: () => {
            const node = editor.selection.getNode();
            if (node && node.tagName !== "BODY") {
              const btnTarget = node.closest("a");
              if (btnTarget && isButton(btnTarget)) {
                openButtonDialog(btnTarget);
              } else {
                openStyleDialog(node);
              }
            }
          },
        });

        // --- Double-click to edit existing button OR element style ---
        editor.on("dblclick", (e) => {
          const btnTarget = e.target.closest("a");
          if (btnTarget && isButton(btnTarget)) {
            openButtonDialog(btnTarget);
            return;
          }

          // For any other block element, open style editor
          const blockTags = ["DIV", "TD", "TH", "SECTION", "P", "SPAN", "TABLE", "H1", "H2", "H3", "H4", "H5", "H6"];
          const el = e.target;
          if (el && blockTags.includes(el.tagName)) {
            openStyleDialog(el);
          }
        });
      },
    };
  }, []);

  return (
    <>
      <Container fluid className="mt-4">
        <Row>
          <Col lg="4">
            <PlaceholderSidebar
              placeholders={placeholders}
              onInsert={insertPlaceholder}
              rightCardHeight={rightCardHeight}
            />
          </Col>

          <Col lg="8">
            <Card className="shadow" innerRef={rightCardRef}>
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">
                    {isEditMode
                    ? t("page.notificationTemplates.buttons.editTemplate", "Edit Template")
                    : t("page.notificationTemplates.buttons.createTemplate", "Create Template")}
                    </h3>
                  </Col>
                  <Col className="text-right" xs="4">
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={handleBack}
                    >
                      {t("common.back", "Back")}
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitClick();
                  }}
                >
                  <FormGroup>
                    <Label className="form-control-label">
                      {t("page.notificationTemplates.templateName", "Template Name")}
                    </Label>
                    <Input
                      type="text"
                      name="name"
                      value={templateData.name}
                      onChange={handleChange}
                      placeholder={t("page.notificationTemplates.templateName", "Template Name")}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label className="form-control-label">
                      {t("table.headers.subject", "Subject")}
                    </Label>
                    <Input
                      innerRef={subjectRef}
                      name="subject"
                      value={templateData.subject}
                      onChange={handleChange}
                      onFocus={() => handleFocus("subject")}
                      placeholder={t("table.headers.subject", "Subject")}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label className="mb-1 form-control-label">
                      {t("page.notificationTemplates.body", "Body Content")}
                    </Label>

                    <Editor
                      tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                      onInit={(evt, editor) => {
                        editorRef.current = editor;
                      }}
                      value={templateData.content}
                      onEditorChange={handleEditorChange}
                      onFocus={() => handleFocus("body")}
                      init={editorInit}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label className="form-control-label">
                      {t("page.notificationTemplates.pushTitle", "Push Title")}
                    </Label>
                    <Input
                      type="text"
                      name="pushTitle"
                      value={templateData.pushTitle}
                      onChange={handleChange}
                      placeholder={t("page.notificationTemplates.pushTitlePlaceholder", "Enter push notification title")}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label className="form-control-label">
                      {t("page.notificationTemplates.pushBody", "Push Body")}
                    </Label>
                    <Input
                      type="textarea"
                      name="pushBody"
                      value={templateData.pushBody}
                      onChange={handleChange}
                      placeholder={t("page.notificationTemplates.pushBodyPlaceholder", "Enter push notification body")}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label className="form-control-label">
                      {t("page.notificationTemplates.channels", "Channels")}
                    </Label>

                    <CreatableSelect
                      isMulti
                      options={channelOptions}
                      value={channelOptions.filter(opt =>
                        templateData.channels?.includes(opt.value)
                      )}
                      onChange={(selected) =>
                        setTemplateData((prev) => ({
                          ...prev,
                          channels: selected.map((s) => s.value),
                        }))
                      }
                    />
                  </FormGroup>

                  <FormGroup check className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        name="isActive"
                        checked={templateData.isActive}
                        onChange={handleChange}
                      />{" "}
                      {t("page.notificationTemplates.enableTemplate", "Enable this template for sending notifications")}
                    </Label>
                  </FormGroup>

                  <div className="d-flex justify-content-end">
                    <Button color="warning" disabled={loading}>
                      {loading && <Spinner size="sm" className="mr-2" />}
                      {isEditMode
                        ? t("page.notificationTemplates.buttons.updateTemplate", "Update Template")
                        : t("page.notificationTemplates.buttons.createTemplate", "Create Template")}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <NotificationTemplatePreviewModal
        isOpen={isPreviewOpen}
        toggle={() => setIsPreviewOpen(false)}
        htmlContent={previewHtml}
      />
    </>
  );
};

export default CreateTemplatePage;